import React, {PropsWithChildren} from 'react';
import {
  DataGridProProps,
  GridColDef,
  GridOverlay,
  GridRowModel,
  GridRowParams,
} from '@mui/x-data-grid-pro';
import {Box} from '@mui/material';
import clsx from 'clsx';
import {useTableStyles} from '../useTableStyles';
import {Panel} from '../Panel/Panel';
import {themeColors} from '../../theme/Theme';
import {StickyDataGrid} from './StickyDataGrid';
import {
  CustomColumnMenuComponent,
  useDataGridState,
} from '../EditorGrid/useDataGridState';
import Loading from '../Loading/Loading';

export type SearchResultsProps = Omit<DataGridProProps, 'rows'> & {
  title: string;
  search: string;
  columns: GridColDef[];
  data: GridRowModel[];
  onRowDoubleClick: (
    param: GridRowParams,
    event: React.MouseEvent<Element, MouseEvent>
  ) => void;
  minHeight?: number | false;
  placeholder?: string;
  alternateRowBg?: boolean;
  entity?: string;
};

export const SearchResults: React.FC<
  PropsWithChildren<SearchResultsProps> & {
    dataGridId: string;
  }
> = (props) => {
  const classes = useTableStyles();
  const {
    dataGridId,
    search,
    columns,
    data,
    onRowDoubleClick,
    className,
    title,
    minHeight = 600,
    alternateRowBg = false,
    entity = 'accounts',
    placeholder = 'Search a name to view',
    initialState: defaultInitialState,
    children,
    ...rest
  } = props;
  const {apiRef, initialState, key, restoreState} = useDataGridState(dataGridId);

  if (initialState === undefined) {
    return <Loading />;
  }
  return (
    <Panel color={themeColors.blue} title={title}>
      {children}
      <Box mt={1} display="flex" minHeight={minHeight || undefined}>
        <StickyDataGrid
          slots={{
            columnMenu: (columnProps) => (
              <CustomColumnMenuComponent restoreState={restoreState} {...columnProps} />
            ),
            noRowsOverlay: () => (
              <GridOverlay title={`No ${entity} found`}>
                {search?.length > 3 ? 'No results found' : placeholder}
              </GridOverlay>
            ),
          }}
          columns={columns}
          isCellEditable={() => false}
          className={clsx(
            'search-results',
            classes.table,
            classes.hideColumnMenu,
            className,
            alternateRowBg ? classes.alternate : null
          )}
          rows={data}
          apiRef={apiRef}
          key={key}
          initialState={initialState || defaultInitialState}
          rowHeight={120}
          autoHeight={minHeight === false}
          onRowDoubleClick={onRowDoubleClick}
          {...rest}
        />
      </Box>
    </Panel>
  );
};
